import { useEffect, useState } from "react"
import { Navigate, useParams } from "react-router"
import styled from "styled-components"

import HostedVideoPlayer from "@forento/shared-client/components/HostedVideoPlayer"
import LexicalViewer from "@forento/shared-client/components/LexicalViewer"
import { exhaustiveGuard } from "@forento/shared/utilities/switch"

import { type PaypalOrder, PurchaseSuccessModalContextProvider } from "~/contexts/PurchaseSuccessModalContext"
import { useUser } from "~/contexts/UserContext"
import Button from "~/themes/school/components/Button"
import Hero from "~/themes/school/components/Hero"
import Layout from "~/themes/school/components/Layout"
import PartialLoadingPage from "~/themes/school/components/PartialLoadingPage"
import { MoreContent } from "~/themes/school/components/ProductDetail"
import PurchaseItem from "~/themes/school/components/PurchaseItem"
import { fonts } from "~/themes/school/styles"
import { useTranslation } from "~/translations"
import routes from "~/utilities/routes"
import { query } from "~/utilities/trpc"

const VideoPage: React.FC = () => {
	const { videoId } = useParams()
	const user = useUser()
	const t = useTranslation()

	const [paypalOrder, setPaypalOrder] = useState<PaypalOrder | null>(null)

	const { data: video, error, refetch } = query.video.getPersonalized.useQuery({ videoId: Number(videoId) })

	const trackWatch = query.video.trackWatch.useMutation()

	useEffect(() => {
		refetch()
	}, [user.user, refetch])

	if (video === null) return <Navigate to={routes.video.index()} />

	return (
		<PurchaseSuccessModalContextProvider content={{ type: "video" }} paypalOrder={paypalOrder}>
			<Layout>
				{error ? (
					<p>{t("failedToLoad")}</p>
				) : video === undefined ? (
					<PartialLoadingPage />
				) : (
					<>
						{video.status === "unlocked" ? (
							<Container>
								<StyledVideoPlayer
									video={video}
									onTrackWatch={progress => trackWatch.mutate({ videoId: video.id, progress })}
								/>
								<Title>{video.name}</Title>
								{video.longDescriptionHtml && <LexicalViewer html={video.longDescriptionHtml} />}
							</Container>
						) : (
							<Hero
								title={video.name}
								thumbnailUrl={video.thumbnailUrl}
								htmlDescription={video.longDescriptionHtml ?? undefined}
								tags={video.tags}
								callToAction={
									video.status === "membership-required" ? (
										<CallToAction>
											<p>{t("product.onlyForMembers")}</p>
											<Button variant="primary" onClick={routes.membership.index()}>
												{t("membership.title")}
											</Button>
										</CallToAction>
									) : video.status === "paywall" ? (
										<PurchaseItem
											item={{
												type: "video",
												id: video.id,
												name: video.name,
												shortDescription: video.shortDescription,
												price: video.price,
												accessTimeMonths: null,
											}}
											onPurchase={async ({ paypalOrder }) => {
												setPaypalOrder(paypalOrder)
												await refetch()
											}}
										/>
									) : video.status === "sign-in-required" ? (
										<CallToAction>
											<p>{t("product.onlyForUsers")}</p>
											<ButtonsRow>
												<Button variant="primary" onClick={routes.user.signin()}>
													{t("user.signIn.title")}
												</Button>
												<Button variant="primary" onClick={routes.user.signup()}>
													{t("user.signUp.title")}
												</Button>
											</ButtonsRow>
										</CallToAction>
									) : video.status === "bokadero-membership-required" ? (
										<CallToAction>
											<p>Gratis för medlemmar i Bokadero Arena</p>
											<ButtonsRow>
												{user.user ? (
													<Button variant="primary" onClick={routes.user.settings()}>
														Hantera inställningar
													</Button>
												) : (
													<Button variant="primary" onClick={routes.user.signup()}>
														Skapa konto
													</Button>
												)}
											</ButtonsRow>
										</CallToAction>
									) : video.status === "paused" ? (
										<CallToAction>
											<p>{t("product.paused")}</p>
										</CallToAction>
									) : (
										exhaustiveGuard(video.status)
									)
								}
							/>
						)}
						{video.presentationHtml !== null && (
							<MoreContent>
								<div
									className="editorjs"
									dangerouslySetInnerHTML={{ __html: video.presentationHtml }}
								/>
							</MoreContent>
						)}
					</>
				)}
			</Layout>
		</PurchaseSuccessModalContextProvider>
	)
}

const Container = styled.div`
	max-width: 1400px;
	background-color: white;
	box-shadow: 0px 4px 30px 0px rgba(223, 232, 255, 0.25);
	padding: 24px;
	margin-bottom: 32px;
`

const StyledVideoPlayer = styled(HostedVideoPlayer)`
	background-color: #999;
	margin-bottom: 16px;
`

const Title = styled.h1`
	font-family: ${fonts.quicksand};
	font-size: 28px;
	font-weight: bold;
	margin-bottom: 12px;
`

const CallToAction = styled.div`
	max-width: 400px;
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 16px;
	text-align: center;
`

const ButtonsRow = styled.div`
	display: flex;
	gap: 16px;

	@media (max-width: 499px) {
		flex-direction: column;
	}
`

export default VideoPage
